<template>
  <div class="w-12">
    <div class="flex flex-row items-center justify-center">
      <slot :toggle="toggle" :data="data">
        <PrimeButton
          type="button"
          size="small"
          icon="pi pi-ellipsis-h"
          @click.stop="($event) => toggle($event, data)"
          aria-haspopup="true"
          aria-controls="overlay_menu"
          class="py-1 bg-transparent border-none hover:bg-[#E2E8F0]"
          severity="secondary"
        />
      </slot>
      <UiDialogCopy
        v-slot="{ toggle }"
        :meta="props.meta"
        :data="data"
        @copy="
          ($event: any) => {
            emits('copy', {
              record: $event.record,
              copyFields: $event.copyFields,
            });
          }
        "
        maximizable
        v-model:visible="copyVisible"
        modal
        v-if="meta.data.Table.ShowCopy"
      >
      </UiDialogCopy>
      <PrimeMenu ref="menu" id="overlay_menu" :popup="true">
        <template #end>
          <button
            type="button"
            v-if="meta.data.Table.ShowEdit && props.showEdit"
            class="flex flex-row items-center text-[14px] gap-1.5 w-full hover:bg-[#E2E8F0] px-2 py-2"
            @click="
              ($event) => {
                emits('edit', { record: data });
                menu.hide();
              }
            "
          >
            <i class="pi pi-pen-to-square text-[14px] text-gray-500"></i>
            Edit
          </button>
          <button
            type="button"
            class="flex flex-row items-center text-[14px] gap-1.5 w-full hover:bg-[#E2E8F0] px-2 py-2"
            @click="
              ($event) => {
                emits('showCard', { record: data });
                menu.hide();
              }
            "
            v-if="props.meta.enabledCardColumns.length > 0"
          >
            <i class="pi pi-info-circle text-[14px] text-gray-500"></i>
            More Info
          </button>
          <!-- Grid actions: inline if ≤4, hover submenu if >4 -->
          <template v-if="gridActions.length <= 4">
            <template v-for="action in gridActions" :key="action.Id">
              <button
                v-if="actionStates[action.Id]?.show"
                type="button"
                class="flex flex-row items-center text-[14px] gap-1.5 w-full hover:bg-[#E2E8F0] px-2 py-2"
                :disabled="
                  actionStates[action.Id]?.disabled ||
                  actionStates[action.Id]?.loading
                "
                @click="executeAction(action)"
              >
                <i
                  :class="`pi ${action.ButtonImageCss} text-[14px] text-gray-500`"
                  v-if="action.ButtonImageCss"
                ></i>
                <i class="pi pi-hashtag text-[14px] text-gray-500" v-else></i>
                {{ action.Name }}
                <i
                  v-if="actionStates[action.Id]?.loading"
                  class="pi pi-spin pi-spinner text-[14px] text-gray-500 ml-auto"
                ></i>
              </button>
            </template>
          </template>
          <div
            v-else
            class="relative"
            @mouseenter="onActionsHoverEnter"
            @mouseleave="actionsHoverVisible = false"
          >
            <button
              type="button"
              class="flex flex-row items-center text-[14px] gap-1.5 w-full hover:bg-[#E2E8F0] px-2 py-2"
            >
              <i class="pi pi-list text-[14px] text-gray-500"></i>
              Actions
              <i :class="actionsSubmenuOnLeft ? 'pi pi-chevron-left' : 'pi pi-chevron-right'" class="text-[14px] text-gray-500 ml-auto"></i>
            </button>
            <div
              v-if="actionsHoverVisible"
              :class="['absolute top-0 bg-white shadow-lg border border-gray-200 rounded min-w-[180px] z-50', actionsSubmenuOnLeft ? 'right-full' : 'left-full']"
            >
              <template v-for="action in gridActions" :key="action.Id">
                <button
                  v-if="actionStates[action.Id]?.show"
                  type="button"
                  class="flex flex-row items-center text-[14px] gap-1.5 w-full hover:bg-[#E2E8F0] px-2 py-2"
                  :disabled="
                    actionStates[action.Id]?.disabled ||
                    actionStates[action.Id]?.loading
                  "
                  @click="executeAction(action)"
                >
                  <i
                    :class="`pi ${action.ButtonImageCss} text-[14px] text-gray-500`"
                    v-if="action.ButtonImageCss"
                  ></i>
                  <i class="pi pi-hashtag text-[14px] text-gray-500" v-else></i>
                  {{ action.Name }}
                  <i
                    v-if="actionStates[action.Id]?.loading"
                    class="pi pi-spin pi-spinner text-[14px] text-gray-500 ml-auto"
                  ></i>
                </button>
              </template>
            </div>
          </div>

          <button
            @click="
              () => {
                copyVisible = true;
              }
            "
            class="flex flex-row items-center text-[14px] gap-1.5 w-full hover:bg-[#E2E8F0] px-2 py-2"
            v-tooltip="'Copy'"
            v-if="meta.data.Table.ShowCopy && props.showCopy"
          >
            <i class="pi pi-copy text-[14px] text-gray-500"></i>
            Copy
          </button>
          <button
            type="button"
            v-if="meta.data.Table.ShowDelete && props.showDelete"
            class="flex flex-row items-center text-[14px] text-red-500 gap-1.5 w-full hover:bg-[#E2E8F0] px-2 py-2"
            @click="
              ($event) => {
                confirm.require({
                  // @ts-ignore
                  target: $event.target,
                  message: 'Are you sure you want to proceed?',
                  icon: 'pi pi-exclamation-triangle',
                  rejectClass:
                    'p-button-secondary p-button-outlined p-button-sm',
                  acceptClass: 'p-button-sm',
                  rejectLabel: 'No',
                  acceptLabel: 'Yes',
                  accept: async () => {
                    emits('delete', { record: data });
                  },
                  reject: () => {},
                });
                menu.hide();
              }
            "
          >
            <i class="pi pi-trash text-[14px] text-red-400"></i>
            Delete
          </button>
        </template>
      </PrimeMenu>

      <!-- Confirmation Dialog for current action -->
      <PrimeDialog
        v-model:visible="showConfirmationDialog"
        :header="currentAction?.ConfirmationHeaderText"
        modal
        @close="onConfirmDialogClose"
        @cancel="onConfirmDialogClose"
        :loading="currentActionLoading"
      >
        <div class="card flex flex-wrap justify-content-center gap-3">
          <div class="flex align-items-center">
            <PrimeCheckbox
              v-model="hasConfirmedDialog"
              inputId="confirmMenuCheck"
              name="confirm-menu-action-check"
              :binary="true"
              :disabled="currentActionLoading"
            />
            <label for="confirmMenuCheck" class="ml-2">
              {{ currentAction?.ConfirmationBodyText }}
            </label>
          </div>
        </div>
        <template #footer>
          <div class="flex gap-2 pt-1">
            <PrimeButton
              :label="currentAction?.Name"
              :icon="currentAction?.ButtonImageCss"
              severity="secondary"
              :disabled="!hasConfirmedDialog || currentActionLoading"
              :loading="currentActionLoading"
              @click="runCurrentAction"
            />
            <PrimeButton
              label="Cancel"
              @click="onConfirmDialogClose"
              :loading="currentActionLoading"
            />
          </div>
        </template>
      </PrimeDialog>

      <!-- User Input Dialog for current action -->
      <FormActionUserInput
        v-model:visible="userInputVisible"
        v-model:data="userInputValues"
        :actionParams="currentActionParams"
        :action="currentAction!"
        @submit="onUserInputSubmit"
        @cancel="onUserInputCancel"
        v-if="currentAction && currentActionParams.length > 0"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  ActionParameterTypeEnum,
  ActionParameterValueTypeEnum,
  type IMetaAction,
  type IMetaComputed,
} from "~/scad-shared/types/scad";

const router = useRouter();

const props = withDefaults(defineProps<{
  data: any;
  table: string;
  schema: any;
  meta: IMetaComputed;
  showEdit?: boolean;
  showDelete?: boolean;
  showCopy?: boolean;
}>(), {
  showEdit: true,
  showDelete: true,
  showCopy: true,
});

const emits = defineEmits<{
  refresh: [payload: { response: any }];
  edit: [payload: { record: any }];
  delete: [payload: { record: any }];
  copy: [payload: { record: any; copyFields: any[] }];
  showCard: [payload: { record: any }];
}>();

const menu = ref();
const confirm = useConfirm();
const toast = useToast();
const copyVisible = ref(false);
const actionsHoverVisible = ref(false);
const actionsSubmenuOnLeft = ref(false);

function onActionsHoverEnter(event: MouseEvent) {
  const el = event.currentTarget as HTMLElement;
  const rect = el.getBoundingClientRect();
  actionsSubmenuOnLeft.value = rect.right + 180 > window.innerWidth;
  actionsHoverVisible.value = true;
}

// Current action being executed
const currentAction = ref<IMetaAction | null>(null);
const currentActionLoading = ref(false);

// Confirmation dialog state
const showConfirmationDialog = ref(false);
const hasConfirmedDialog = ref(false);

// User input state
const userInputVisible = ref(false);
const userInputValues = ref<Indexable>({});

// // Auth info for expressions
// const { authInfo } = useAuthStore();

// Expression evaluator
const evaluator = new ExpressionEvaluator();

// Grid actions
const gridActions = computed(() => {
  return props.meta.data.Actions?.filter((a) => a.GridAction) ?? [];
});

// Create useMetaAction instances for each grid action at setup time
// This ensures composables are called at the proper lifecycle stage
const actionExecutors = new Map<number, ReturnType<typeof useMetaAction>>();

// Initialize executors for all grid actions immediately during setup
for (const action of props.meta.data.Actions?.filter((a) => a.GridAction) ??
  []) {
  actionExecutors.set(action.Id, useMetaAction(action, props.data));
}

// Compute action states (show/disabled/loading) for all grid actions
const actionStates = computed(() => {
  const states: Record<
    number,
    { show: boolean; disabled: boolean; loading: boolean }
  > = {};

  for (const action of gridActions.value) {
    const evaluationContext = {
      ...props.data,
      // _auth_: authInfo,
      _extra: {},
    };

    let show = true;
    if (action.ShowExpression) {
      try {
        show = evaluator.evaluateExpression(
          action.ShowExpression,
          evaluationContext,
        );
      } catch {
        show = true;
      }
    }

    let disabled = false;
    if (action.DisabledExpression) {
      try {
        disabled = evaluator.evaluateExpression(
          action.DisabledExpression,
          evaluationContext,
        );
      } catch {
        disabled = false;
      }
    }

    states[action.Id] = {
      show,
      disabled,
      loading:
        currentAction.value?.Id === action.Id && currentActionLoading.value,
    };
  }

  return states;
});

// Get user input params for current action
const currentActionParams = computed(() => {
  if (!currentAction.value) return [];
  return (
    currentAction.value.ActionParameters?.filter(
      (p) =>
        p.ActionParameterType === ActionParameterTypeEnum.Request &&
        p.ActionParameterValueType === ActionParameterValueTypeEnum.UserInput,
    ) ?? []
  );
});

// Check if current action has confirmation dialog
const hasConfirmationDialogMeta = computed(() => {
  if (!currentAction.value) return false;
  if (currentAction.value.UseConfirmationDialog === false) return false;
  if (
    currentAction.value.UseConfirmationDialog === true &&
    (currentAction.value.ConfirmationHeaderText?.length ?? 0) > 0 &&
    (currentAction.value.ConfirmationBodyText?.length ?? 0) > 0
  ) {
    return true;
  }
  return false;
});

const toggle = (event: Event, data: any) => {
  menu.value.toggle(event);
};

function onConfirmDialogClose() {
  showConfirmationDialog.value = false;
  hasConfirmedDialog.value = false;
}

function onUserInputCancel() {
  userInputVisible.value = false;
  userInputValues.value = {};
  currentAction.value = null;
}

async function onUserInputSubmit(values: Indexable) {
  userInputVisible.value = false;
  // Capture the emitted values to ensure we have the latest data
  if (values && Object.keys(values).length > 0) {
    userInputValues.value = { ...userInputValues.value, ...values };
  }
  await runCurrentAction();
}

async function executeAction(action: IMetaAction) {
  currentAction.value = action;
  userInputValues.value = {};

  // Check if user input is required
  const userInputParams =
    action.ActionParameters?.filter(
      (p) =>
        p.ActionParameterType === ActionParameterTypeEnum.Request &&
        p.ActionParameterValueType === ActionParameterValueTypeEnum.UserInput,
    ) ?? [];

  if (userInputParams.length > 0) {
    // Start each field from its parameter's Value (meta_action_parameter.value): a ticked box, a filled date.
    userInputValues.value = userInputDefaults(userInputParams);
    userInputVisible.value = true;
    return;
  }

  // Check if confirmation is needed
  if (hasConfirmationDialogMeta.value && !hasConfirmedDialog.value) {
    showConfirmationDialog.value = true;
    return;
  }

  await runCurrentAction();
}

async function runCurrentAction() {
  if (!currentAction.value) {
    console.warn("runCurrentAction called without currentAction");
    return;
  }

  // Check if confirmation is needed and not yet confirmed
  if (hasConfirmationDialogMeta.value && !hasConfirmedDialog.value) {
    showConfirmationDialog.value = true;
    return;
  }

  const action = currentAction.value;

  // Get the pre-created executor for this action
  const executor = actionExecutors.get(action.Id);
  if (!executor) {
    console.warn("No executor found for action:", action.Id);
    return;
  }

  const { execute, Response } = executor;

  currentActionLoading.value = true;

  try {
    const contextData = {
      ...props.data,
      ...(userInputValues.value ?? {}),
      _baseUrl: import.meta.client ? window.location.origin : undefined,
      _extra: {},
    };

    console.log("Executing action:", action.Name, "with context:", contextData);

    const res = await execute(contextData);

    console.log("Action result:", res);

    switch (res?.action?.toLowerCase()) {
      case "error":
        toast.add({
          severity: "error",
          summary: "Error",
          detail: res.error?.message ?? String(res.error),
          life: 5000,
        });
        break;
      case "filedownload":
        const fileName = getFileNameFromHeaders(res.headers);
        downloadFile(res.data as Blob, fileName ?? "downloadedFile");
        break;
      case "navigate":
        if (res.url) {
          window.open(res.url, "_blank");
        }
        break;
    }

    console.log("Action Menu Complete handling");
    menu.value.hide();
    emits("refresh", { response: { response: Response.value, ...res } });
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Unexpected Error",
      detail: error instanceof Error ? error.message : String(error),
      life: 5000,
    });
  } finally {
    currentActionLoading.value = false;
    onConfirmDialogClose();
    currentAction.value = null;
    userInputValues.value = {};
  }
}
</script>

<style scoped></style>
